import api from './api'

// Ids are typed `number | string` and are placed in URL paths, so they are
// encoded before interpolation: a value like `1/../../admin/users` would
// otherwise escape the endpoint. No-op for numeric ids.
const segment = (id: number | string): string => encodeURIComponent(String(id))

/**
 * Journal Approval Workflow + Instance API.
 *
 * Every route below is registered and verified against
 * app/Modules/Accounting/Routes/api.php:
 *   Workflows:  GET|POST   /journal-approval-workflows
 *               GET|PUT|DELETE /journal-approval-workflows/{id}
 *   Instances:  GET        /journal-approval-instances/pending
 *               POST       /journal-approval-instances/{id}/approve
 *               POST       /journal-approval-instances/{id}/reject
 *
 * The pre-approval per-journal actions (`POST /journals/{id}/approve`,
 * `/approve-closing`, `/reject-closing`) live on the journals routes and are
 * NOT part of this contract.
 */
export const journalApprovalWorkflowsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> =>
    api.get('/journal-approval-workflows', { params }),
  getById: (id: number | string): Promise<any> =>
    api.get(`/journal-approval-workflows/${segment(id)}`),
  create: (data: Record<string, any>): Promise<any> =>
    api.post('/journal-approval-workflows', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> =>
    api.put(`/journal-approval-workflows/${segment(id)}`, data),
  delete: (id: number | string): Promise<any> =>
    api.delete(`/journal-approval-workflows/${segment(id)}`),
}

export const journalApprovalInstancesApi = {
  getPending: (params: Record<string, any> = {}): Promise<any> =>
    api.get('/journal-approval-instances/pending', { params }),
  approve: (id: number | string, data: Record<string, any> = {}): Promise<any> =>
    api.post(`/journal-approval-instances/${segment(id)}/approve`, data),
  reject: (id: number | string, data: Record<string, any> = {}): Promise<any> =>
    api.post(`/journal-approval-instances/${segment(id)}/reject`, data),
}

// RTK Query endpoints (typed cache + automatic invalidation). Prefer these
// hooks in components; the axios helpers above remain for legacy callers.
import { apiSlice } from '@/redux/api/apiSlice'

export const journalApprovalRtkApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getApprovalWorkflows: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/journal-approval-workflows',
        params,
      }),
      providesTags: ['Accounting'],
    }),
    getPendingApprovalInstances: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/journal-approval-instances/pending',
        params,
      }),
      providesTags: ['Accounting'],
    }),
    approveJournalInstance: builder.mutation<any, { id: number | string; data?: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/journal-approval-instances/${segment(id)}/approve`,
        method: 'POST',
        // Default to an empty object: forwarding `undefined` sent no body at all,
        // while the other helpers in this service always post at least `{}`.
        data: data ?? {},
      }),
      invalidatesTags: ['Accounting'],
    }),
    rejectJournalInstance: builder.mutation<any, { id: number | string; data?: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/journal-approval-instances/${segment(id)}/reject`,
        method: 'POST',
        data: data ?? {},
      }),
      invalidatesTags: ['Accounting'],
    }),
  }),
})

export const {
  useGetApprovalWorkflowsQuery,
  useGetPendingApprovalInstancesQuery,
  useApproveJournalInstanceMutation,
  useRejectJournalInstanceMutation,
} = journalApprovalRtkApi

